import React, { useState } from "react";
import { useGetPlatformAdminOrganisationsQuery } from "@/store/admin_api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { FaCheck, FaTimes } from "react-icons/fa";
import { Link } from "react-router-dom";

export default function OrganisationsList() {
  const { data: organisations, isLoading } = useGetPlatformAdminOrganisationsQuery();
  const [searchQuery, setSearchQuery] = useState("");

  const filteredOrganisations = organisations?.filter((org) => {
    const query = searchQuery.toLowerCase();
    return org.name.toLowerCase().includes(query) || org.id.toLowerCase().includes(query);
  });

  const getSubscriptionBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      TRIAL: "default",
      ACTIVE: "default",
      PAST_DUE: "destructive",
      TRIAL_LAPSED: "destructive",
      CANCELED: "secondary",
      SUSPENDED: "destructive",
    };
    return <Badge variant={variants[status] || "outline"}>{status.replace("_", " ")}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">All Organisations</h2>
        <p className="text-muted-foreground">Manage all organisations and their Twilio credentials</p>
      </div>

      <div className="mb-4">
        <Input
          type="text"
          placeholder="Search by name or ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Subscription</TableHead>
              <TableHead>Balance</TableHead>
              <TableHead>KYC Status</TableHead>
              <TableHead>Valid Phone</TableHead>
              <TableHead>Valid Email</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredOrganisations && filteredOrganisations.length > 0 ? (
              filteredOrganisations.map((org) => (
                <TableRow key={org.id}>
                  <TableCell className="font-medium">{org.name}</TableCell>
                  <TableCell className="font-mono text-sm">{org.id}</TableCell>
                  <TableCell>{getSubscriptionBadge(org.subscriptionStatus)}</TableCell>
                  <TableCell>${org.balance?.toFixed(2) || "0.00"}</TableCell>
                  <TableCell>
                    <Badge variant={org.kycVerificationStatus === "VERIFIED" ? "default" : "outline"}>
                      {org.kycVerificationStatus.replace("_", " ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {org.validPhone ? <FaCheck className="text-green-600" /> : <FaTimes className="text-red-600" />}
                  </TableCell>
                  <TableCell>
                    {org.validEmail ? <FaCheck className="text-green-600" /> : <FaTimes className="text-red-600" />}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/platform-admin/organisations/${org.id}`}>Manage</Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  No organisations found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
